/**
 * Parses CSV string in two formats:
 * 1. Native GTM format (months as columns, metrics as rows)
 * 2. Standard forecast format (months as rows, metrics as columns)
 * @param {string} csvString - Raw CSV data
 * @returns {object} Validated and parsed data
 * @throws {Error} If validation fails
 */
export function parseCSV(csvString) {
  if (!csvString || csvString.trim().length === 0) {
    throw new Error('CSV data is empty');
  }

  const lines = csvString.trim().split('\n');
  if (lines.length < 2) {
    throw new Error('CSV must contain headers and at least one data row');
  }

  // Check if this is native GTM format (metrics as rows, months as columns)
  if (isNativeGTMFormat(lines)) {
    return parseNativeGTMFormat(lines);
  }

  // Otherwise, parse as standard forecast format
  return parseStandardFormat(lines);
}

/**
 * Detects if the CSV is in native GTM format
 */
function isNativeGTMFormat(lines) {
  const firstCol = lines[1]?.split(',')?.[0]?.trim();
  // Check if second row starts with a metric name (SQL, SQO, etc.)
  return ['SQL', 'SQO', 'Pilot', 'Closed Won'].includes(firstCol);
}

/**
 * Parses native GTM format (months as columns, metrics as rows)
 */
function parseNativeGTMFormat(lines) {
  // First line contains months
  const monthsLine = lines[0].split(',').map(m => m.trim());
  const months = monthsLine.slice(1, -1); // Skip empty first col and TOTAL

  // Find and extract key rows
  const metricsMap = {};
  lines.forEach(line => {
    const parts = line.split(',').map(p => p.trim());
    const metricName = parts[0];
    const values = parts.slice(1, -1); // Skip metric name and TOTAL

    if (metricName === 'SQL') metricsMap.sql = values;
    else if (metricName === 'SQO') metricsMap.sqo = values;
    else if (metricName === 'Closed Won') metricsMap.closedWon = values;
    else if (metricName === 'Closed Won $') metricsMap.closedWonValue = values;
    else if (metricName === 'Ramped AE') metricsMap.rampedAE = values;
  });

  // Validate we have the required metrics
  if (!metricsMap.sql || !metricsMap.sqo || !metricsMap.closedWon) {
    throw new Error('Missing required metrics: SQL, SQO, and Closed Won');
  }

  // Transform to standard format (rows = months)
  const rows = [];
  months.forEach((month, idx) => {
    const sql = parseFloat(metricsMap.sql[idx]) || 0;
    const sqo = parseFloat(metricsMap.sqo[idx]) || 0;
    const closedWon = parseFloat(metricsMap.closedWon[idx]) || 0;

    // Parse closed won value (remove $ and commas)
    const cwValue = metricsMap.closedWonValue?.[idx]?.replace(/[$,]/g, '');
    const closedWonValue = parseFloat(cwValue) || 0;

    // Calculate metrics
    const sqlToSqoRate = sql > 0 ? sqo / sql : 0;
    const sqoToWonRate = sqo > 0 ? closedWon / sqo : 0;
    const avgDealValue = closedWon > 0 ? closedWonValue / closedWon : 0;
    const numAEs = parseFloat(metricsMap.rampedAE?.[idx]) || 0;

    // Calculate pipeline per AE
    let pipelinePerAE = 0;
    if (numAEs > 0) {
      pipelinePerAE = (sqo * avgDealValue) / numAEs;
    }

    // Skip months with 0 AEs (no team yet)
    if (numAEs > 0) {
      rows.push({
        Month: `2025-${String(idx + 1).padStart(2, '0')}`,
        SQLs: sql,
        SQL_to_SQO_Conversion_Rate: Math.min(sqlToSqoRate, 1),
        SQO_to_Closed_Won_Rate: Math.min(sqoToWonRate, 1),
        Average_Deal_Value_USD: avgDealValue,
        Num_AEs: numAEs,
        Pipeline_Per_AE_USD: pipelinePerAE
      });
    }
  });

  return {
    headers: ['Month', 'SQLs', 'SQL_to_SQO_Conversion_Rate', 'SQO_to_Closed_Won_Rate', 'Average_Deal_Value_USD', 'Num_AEs', 'Pipeline_Per_AE_USD'],
    rows,
  };
}

/**
 * Parses standard forecast format (months as rows, metrics as columns)
 */
function parseStandardFormat(lines) {
  // Parse headers
  const headers = lines[0].split(',').map(h => h.trim());

  // Required columns
  const requiredColumns = [
    'Month',
    'SQLs',
    'SQL_to_SQO_Conversion_Rate',
    'SQO_to_Closed_Won_Rate',
    'Average_Deal_Value_USD',
    'Num_AEs',
    'Pipeline_Per_AE_USD'
  ];

  // Validate all required columns exist
  const missingColumns = requiredColumns.filter(col => !headers.includes(col));
  if (missingColumns.length > 0) {
    throw new Error(`Missing required columns: ${missingColumns.join(', ')}`);
  }

  // Parse data rows
  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split(',').map(v => v.trim());
    if (values.length !== headers.length) {
      throw new Error(`Row ${i} has ${values.length} columns but expected ${headers.length}`);
    }

    const row = {};
    headers.forEach((header, idx) => {
      const value = values[idx];

      if (header === 'Month') {
        row[header] = value;
      } else {
        // Parse numeric values
        const numValue = parseFloat(value);
        if (isNaN(numValue)) {
          throw new Error(`Invalid numeric value "${value}" in column "${header}" at row ${i}`);
        }
        row[header] = numValue;
      }
    });

    // Validate ranges
    if (row.SQLs < 0) throw new Error(`SQLs cannot be negative at row ${i}`);
    if (row.SQL_to_SQO_Conversion_Rate < 0 || row.SQL_to_SQO_Conversion_Rate > 1) {
      throw new Error(`SQL_to_SQO_Conversion_Rate must be between 0 and 1 at row ${i}`);
    }
    if (row.SQO_to_Closed_Won_Rate < 0 || row.SQO_to_Closed_Won_Rate > 1) {
      throw new Error(`SQO_to_Closed_Won_Rate must be between 0 and 1 at row ${i}`);
    }
    if (row.Average_Deal_Value_USD < 0) throw new Error(`Average_Deal_Value_USD cannot be negative at row ${i}`);
    if (row.Num_AEs < 0) throw new Error(`Num_AEs cannot be negative at row ${i}`);
    if (row.Pipeline_Per_AE_USD < 0) throw new Error(`Pipeline_Per_AE_USD cannot be negative at row ${i}`);

    rows.push(row);
  }

  // Check chronological order
  for (let i = 1; i < rows.length; i++) {
    if (rows[i].Month < rows[i - 1].Month) {
      throw new Error(`Rows are not in chronological order: ${rows[i - 1].Month} -> ${rows[i].Month}`);
    }
  }

  return {
    headers,
    rows,
  };
}
